import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const tabs = [
  { id: 'standard', labelKey: 'productsDetail.cex.tabs.standard', icon: 'ri-server-line' },
  { id: 'cluster', labelKey: 'productsDetail.cex.tabs.cluster', icon: 'ri-cloud-line' },
  { id: 'custom', labelKey: 'productsDetail.cex.tabs.custom', icon: 'ri-vip-diamond-line' },
  { id: 'pc', labelKey: 'productsDetail.cex.tabs.pc', icon: 'ri-computer-line' },
  { id: 'h5', labelKey: 'productsDetail.cex.tabs.h5', icon: 'ri-smartphone-line' },
];

interface CexHeroProps {
  activeTab: string;
  onTabChange: (id: string) => void;
}

export default function CexHero({ activeTab, onTabChange }: CexHeroProps) {
  const { t } = useTranslation();

  return (
    <section className="relative bg-white pt-[72px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="http://geel.fp.08wx.cn/images/4fd77c8cc857134a10f8310eecfbf46d.jpg"
          alt="CEX Hero"
          className="w-full h-full object-cover object-top opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/60 to-white/95" />
      </div>

      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,45,120,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,45,120,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 py-16 md:py-24">
        <div className="flex items-center gap-2 mb-8 flex-wrap">
          <Link to="/" className="text-xs text-gray-400 hover:text-primary transition-colors whitespace-nowrap">{t('breadcrumb.home')}</Link>
          <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
          <Link to="/products" className="text-xs text-gray-400 hover:text-primary transition-colors whitespace-nowrap">{t('breadcrumb.products')}</Link>
          <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
          <span className="text-xs text-primary whitespace-nowrap">{t('productsDetail.cex.breadcrumb')}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <i className="ri-exchange-dollar-line text-primary text-xs" />
              <span className="text-primary text-xs font-medium uppercase tracking-widest">CEX Platform</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-5 leading-tight">
              {t('productsDetail.cex.title')}
              <br />
              <span className="gradient-text">{t('productsDetail.cex.highlight')}</span>
            </h1>
            <p className="text-gray-500 text-base leading-relaxed mb-8 max-w-lg">
              {t('productsDetail.cex.desc1')}
              {t('productsDetail.cex.desc2')}
              {t('productsDetail.cex.desc3')}
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'ri-speed-line', labelKey: 'productsDetail.cex.tags.0' },
                { icon: 'ri-shield-check-line', labelKey: 'productsDetail.cex.tags.1' },
                { icon: 'ri-device-line', labelKey: 'productsDetail.cex.tags.2' },
                { icon: 'ri-24-hours-line', labelKey: 'productsDetail.cex.tags.3' },
              ].map((tag, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-primary/20 rounded-lg text-xs text-gray-600 shadow-sm">
                  <i className={`${tag.icon} text-primary text-xs`} />
                  {t(tag.labelKey)}
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold text-sm rounded-lg hover:bg-primary-dark transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-customer-service-line" />
              {t('productsDetail.cex.cta')}
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { val: '100,000+', unit: 'TPS', unitKey: '', labelKey: 'productsDetail.cex.stats.0.label', icon: 'ri-speed-line', color: '#FF2D78' },
              { val: '99.99%', unit: '', unitKey: '', labelKey: 'productsDetail.cex.stats.1.label', icon: 'ri-shield-check-line', color: '#FF8C42' },
              { val: '< 1ms', unit: '', unitKey: '', labelKey: 'productsDetail.cex.stats.2.label', icon: 'ri-time-line', color: '#FF2D78' },
            ].map((stat, i) => (
              <div key={i} className="bg-white border border-primary/15 rounded-xl p-5 relative overflow-hidden hover:border-primary/30 transition-all duration-200" style={{ boxShadow: '0 2px 12px rgba(255,45,120,0.06)' }}>
                <div className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-5 blur-2xl" style={{ background: stat.color }} />
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
                </div>
                <div className="text-2xl font-heading font-black text-gray-900">{stat.val}<span className="text-sm ml-0.5" style={{ color: stat.color }}>{stat.unitKey ? t(stat.unitKey) : stat.unit}</span></div>
                <div className="text-xs text-gray-400 mt-1">{t(stat.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative border-t border-primary/10 bg-light-alt">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm whitespace-nowrap border-b-2 transition-all duration-200 cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                <i className={`${tab.icon} text-sm`} />
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
